/**
 * Página pública de un documento legal: `/legal/terminos` y
 * `/legal/privacidad`. No requiere sesión.
 *
 * Es la versión canónica del texto: la landing redirige sus `/legal/*` aquí
 * para no mantener dos copias que se desincronicen (sep 2026).
 */
import { Link, Navigate, useParams } from "react-router-dom";

import { LegalDocumentBody } from "@/components/legal/LegalDocumentBody";
import { LEGAL_DOCUMENTS, LEGAL_DOC_IDS, isLegalDocId } from "@/legal/documentos";

export function LegalPublico() {
  const { doc: param } = useParams<{ doc: string }>();
  if (!isLegalDocId(param)) {
    return <Navigate to="/legal/privacidad" replace />;
  }
  const doc = LEGAL_DOCUMENTS[param];

  return (
    <div className="min-h-screen bg-surface px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <a href="https://kaptar.com.co" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
                <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
                <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <span className="text-lg font-extrabold tracking-tight text-slate-900">Kaptar</span>
          </a>
          <nav aria-label="Documentos legales" className="flex gap-1">
            {LEGAL_DOC_IDS.map((id) => (
              <Link
                key={id}
                to={`/legal/${id}`}
                aria-current={id === doc.id ? "page" : undefined}
                className={
                  "rounded-md px-3 py-1.5 text-sm font-semibold " +
                  (id === doc.id
                    ? "bg-brand-50 text-brand-700"
                    : "text-slate-600 hover:bg-white hover:text-slate-900")
                }
              >
                {LEGAL_DOCUMENTS[id].shortTitle}
              </Link>
            ))}
          </nav>
        </header>

        <main>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-10">
            <h1 className="text-2xl font-extrabold tracking-tight text-balance text-slate-900 sm:text-3xl">
              {doc.title}
            </h1>
            <div className="mt-3 mb-6 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
              <span className="rounded-full border border-slate-200 px-2.5 py-0.5">
                Versión {doc.version}
              </span>
              <span className="rounded-full border border-slate-200 px-2.5 py-0.5">
                Vigente desde el {doc.vigenteDesde}
              </span>
            </div>
            <LegalDocumentBody doc={doc} />
          </article>
        </main>
      </div>
    </div>
  );
}
